import React from 'react';
import { Box, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MapBackBatteryIcon } from '~/assets/icons/map-back-battery.svg';
import { JourneyPoint } from '~/utils/interface/mapback';
import { getJourneySegmentTypeColor } from '../constants/segmentTypeColors';
import { useJourneyPersonStatuses } from '../context/JourneyPersonStatusContext';

interface JourneyHoverInfoCardProps {
  point: JourneyPoint | null;
  fullName: string;
  startAddress?: string;
}

const JourneyHoverInfoCard: React.FC<JourneyHoverInfoCardProps> = ({ point, fullName, startAddress }) => {
  const { t } = useTranslation('admin/journey');
  const personStatuses = useJourneyPersonStatuses();
  if (!point) return null;

  const stateColor = getJourneySegmentTypeColor(point.segmentType, personStatuses);
  const safeName = fullName?.trim() || '--';
  const speedText = Number.isFinite(point.speed) ? `${point.speed}km/h` : '--';
  const timeText = point.timestamp ? dayjs(point.timestamp).format('DD/MM/YYYY HH:mm') : '--';
  const addressText = startAddress?.trim() || point.address?.trim() || '--';
  const batteryText = Number.isFinite(point.battery_percent)
    ? `${Math.max(0, Math.round(point.battery_percent))}%`
    : '--';

  return (
    <Box
      className='pointer-events-none absolute left-[13px] top-[16px] z-20 flex min-h-[134px] w-[250px] flex-col gap-[10px] rounded-[12px] border p-3 shadow-[0px_0px_6px_0px_#509EE833]'
      style={{ borderColor: stateColor, backgroundColor: '#FFFFFF' }}
    >
      <Box className='flex items-center justify-between gap-2'>
        <Typography
          component='span'
          className='truncate text-[14px] font-semibold leading-[140%]'
          style={{ fontFamily: 'Manrope, sans-serif', letterSpacing: '0.02em', color: stateColor }}
        >
          {safeName}
        </Typography>

        <Box
          component='span'
          className='flex h-6 w-16 items-center justify-center gap-1 rounded-[8px] bg-[#DCE8F5] px-[6px] py-[2px]'
        >
          <MapBackBatteryIcon className='h-4 w-4' />
          <Typography component='span' className='text-[12px] font-semibold leading-[140%] text-[#1F487C]'>
            {batteryText}
          </Typography>
        </Box>
      </Box>

      <Box className='-mx-3 border-t' style={{ borderColor: stateColor }} />

      <Box className='space-y-1 text-[14px] font-normal leading-[140%]' style={{ fontFamily: 'Manrope, sans-serif' }}>
        <Box className='flex items-center gap-2'>
          <Typography component='span' className='min-w-[62px] text-[#5E6773]'>
            {t('hover.speed')}:
          </Typography>
          <Typography component='span' className='text-[#161A1F]'>
            {speedText}
          </Typography>
        </Box>
        <Box className='flex items-center gap-2'>
          <Typography component='span' className='min-w-[62px] text-[#5E6773]'>
            {t('hover.time')}:
          </Typography>
          <Typography component='span' className='text-[#161A1F]'>
            {timeText}
          </Typography>
        </Box>
        <Box className='flex items-start gap-2'>
          <Typography component='span' className='min-w-[62px] text-[#5E6773]'>
            {t('hover.location')}:
          </Typography>
          <Typography component='span' className='min-w-0 flex-1 whitespace-normal break-words text-[#161A1F]'>
            {addressText}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default JourneyHoverInfoCard;
